import { Component } from "react";
import { resolveBlankLogoAsset } from "./blankLogoAssets";
// Deliberately from scoreboardDefaults.js, not ScoreboardFrame.jsx: as of
// #1798, ScoreboardFrame itself renders a nested ScoreboardErrorBoundary
// around its content slot, so importing the constant back from
// ScoreboardFrame.jsx here would be a two-file circular import. See
// scoreboardDefaults.js's header.
import { DEFAULT_BACKGROUND_COLOR } from "./scoreboardDefaults";

// Card #1798 AC2 — a React error boundary whose fallback is Blank, never a
// stack trace or a blank white page. Deliberately self-contained rather than
// reusing BlankLayout/BlankLayout.css: BlankLayout's `.scoreboard-blank` is
// `width/height: 100%` and relies on an ancestor (ScoreboardFrame's
// `.scoreboard-frame__content`, itself inside the viewport-fixed
// `.scoreboard-frame`) to actually size and darken it. A render exception
// can be thrown by ANYTHING in the tree this boundary wraps, including code
// that never reaches that ancestor's DOM at all — so this fallback carries
// its own fixed/full-viewport/dark-background styling inline, and resolves
// the logo through the exact same allow-list BlankLayout does, so it is
// correct standing completely alone. Same data-testids as BlankLayout
// (`scoreboard-blank`/`scoreboard-blank-logo`) on purpose: a Cypress
// assertion that "the board shows Blank" doesn't need to know or care
// whether Blank came from the normal dispatch or from a caught exception.
const FALLBACK_STYLE = {
  position: "fixed",
  inset: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: DEFAULT_BACKGROUND_COLOR,
};

const FALLBACK_LOGO_STYLE = {
  height: "60vh",
  maxWidth: "80vw",
  objectFit: "contain",
};

export default class ScoreboardErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // #1798 AC8 — "falling back to Blank still surfaces the fault to the
  // remote control." A render exception is the one failure mode this card's
  // remote-side classifyBoardRenderState (boardRenderState.js) CANNOT detect
  // from the composed payload's shape alone — the mode/fields can look
  // perfectly valid and still blow up a layout component on a bad nested
  // value. ScoreboardPage.jsx closes that gap by wrapping its own real,
  // visible board in two nested boundaries and reading the result back through
  // `onRenderStatus` + `postScoreboardStatus` — this callback is that
  // read-back point, not a second, independent diagnostic channel.
  // console.error also runs unconditionally so anything watching this
  // kiosk's own devtools/log capture still sees it, but that is
  // belt-and-braces, not the operator-facing half of AC8.
  componentDidCatch(error, info) {
    console.error("[scoreboard] render error, falling back to Blank", error, info?.componentStack);
    this.props.onRenderStatus?.(true);
  }

  componentDidMount() {
    // NOTE: componentDidMount DOES still run even on an instance whose very
    // first render threw — React commits the boundary fiber with the
    // fallback, runs this lifecycle, and only THEN processes the error
    // update queue and calls componentDidCatch above. This method is
    // correct only because of the `!this.state.hasError` guard below, not
    // because mount is somehow skipped; don't remove the guard on the
    // strength of "mount never fires on a caught first-render error", that
    // premise is false. With the guard, this is the "render succeeded" half
    // of the onRenderStatus contract, and componentDidCatch is the "render
    // threw" half.
    if (!this.state.hasError) this.props.onRenderStatus?.(false);
  }

  componentDidUpdate(prevProps) {
    // The public board (ScoreboardPage.jsx) never remounts this boundary —
    // it persists across polls and passes a `resetKey` (the latest
    // `serverTime`) so a caught error clears the moment fresh data arrives,
    // giving the board a chance to recover instead of staying on Blank
    // forever once one bad payload has been through it. This recovery
    // mechanism itself is unchanged by #1798's postMessage work below — it
    // still just clears `hasError` off a resetKey change, no remount, no
    // loop.
    //
    // #1798 AC8 — the `onRenderStatus(false)` call is new: componentDidMount
    // only ever fires once per instance, so without this, a caller tracking
    // "is the board currently render-erroring" (ScoreboardPage's own
    // postMessage status) would see `true` forever after the FIRST caught
    // error, even once this exact recovery has silently fixed it on a later
    // poll. If the next render throws again, componentDidCatch above fires
    // again right after and reasserts `true` — this optimistic `false` is
    // never the last word when the fault persists.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
      this.props.onRenderStatus?.(false);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={FALLBACK_STYLE} data-testid="scoreboard-blank">
          <img
            style={FALLBACK_LOGO_STYLE}
            src={resolveBlankLogoAsset(this.props.logoAssetUrl)}
            alt=""
            data-testid="scoreboard-blank-logo"
          />
        </div>
      );
    }
    return this.props.children;
  }
}
